import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { getAuth } from 'firebase/auth';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import BASE_URL from '../../config';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const STALE_COLOR = '#9e9e9e';

// Continuous green -> amber -> red scale for 0..100% infestation.
const SCALE = [
  { at: 0, color: [46, 125, 50] },    // #2e7d32
  { at: 50, color: [249, 168, 37] },  // #f9a825
  { at: 100, color: [198, 40, 40] },  // #c62828
];

const toHex = ([r, g, b]) =>
  `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;

const infestationColor = (pct) => {
  if (pct == null || Number.isNaN(Number(pct))) return STALE_COLOR;
  const value = Math.max(0, Math.min(100, Number(pct)));
  for (let i = 1; i < SCALE.length; i += 1) {
    const lo = SCALE[i - 1];
    const hi = SCALE[i];
    if (value <= hi.at) {
      const t = (value - lo.at) / (hi.at - lo.at || 1);
      return toHex(lo.color.map((c, idx) => c + (hi.color[idx] - c) * t));
    }
  }
  return toHex(SCALE[SCALE.length - 1].color);
};

const extendBounds = (bounds, geometry) => {
  if (!geometry) return;
  const walk = (coords) => {
    if (typeof coords[0] === 'number') {
      bounds.extend(coords);
    } else {
      coords.forEach(walk);
    }
  };
  walk(geometry.coordinates);
};

/**
 * Org-wide lake infestation map. Fetches every lake's polygon and its latest
 * infestation % (within the last 3 months); colors each lake green->red, or
 * grey when no recent data. Self-contained: fetches its own data.
 */
function LakeInfestationMap({ organisationId }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [features, setFeatures] = useState(null);

  // Fetch lake polygons + infestation levels
  useEffect(() => {
    if (!organisationId) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const user = getAuth().currentUser;
        const token = user && (await user.getIdToken());
        const res = await fetch(
          `${BASE_URL}/api/lake/infestation-map?organisation_id=${organisationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to load lake infestation map');
        const rows = await res.json();
        if (cancelled) return;

        const featureCollection = {
          type: 'FeatureCollection',
          features: (rows || [])
            .filter((row) => row.geometry)
            .map((row) => ({
              type: 'Feature',
              geometry: row.geometry,
              properties: {
                name: row.name,
                infestationPct: row.infestation_pct,
                stale: row.stale,
                recordedAt: row.recorded_at,
                color: row.stale ? STALE_COLOR : infestationColor(row.infestation_pct),
              },
            })),
        };
        setFeatures(featureCollection);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  // Render / update the map once features are available
  useEffect(() => {
    if (!features || !containerRef.current) return undefined;

    if (!mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: containerRef.current,
        style: {
          version: 8,
          sources: {
            'google-satellite': {
              type: 'raster',
              tiles: ['https://mt0.google.com/vt/lyrs=s&hl=en&x={x}&y={y}&z={z}'],
              tileSize: 256,
              attribution: 'Google',
            },
          },
          layers: [
            {
              id: 'google-satellite-layer',
              type: 'raster',
              source: 'google-satellite',
              minzoom: 0,
              maxzoom: 22,
            },
          ],
        },
        center: [78.9, 20.6],
        zoom: 3,
      });
      mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-left');
    }

    const map = mapRef.current;

    const applyData = () => {
      if (map.getSource('lakes')) {
        map.getSource('lakes').setData(features);
      } else {
        map.addSource('lakes', { type: 'geojson', data: features });
        map.addLayer({
          id: 'lakes-fill',
          type: 'fill',
          source: 'lakes',
          paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.6 },
        });
        map.addLayer({
          id: 'lakes-outline',
          type: 'line',
          source: 'lakes',
          paint: { 'line-color': '#ffffff', 'line-width': 1 },
        });

        map.on('click', 'lakes-fill', (e) => {
          const props = e.features?.[0]?.properties;
          if (!props) return;
          const label = props.stale === true || props.stale === 'true'
            ? 'No data in last 3 months'
            : `Infestation: ${props.infestationPct}%${props.recordedAt ? ` (${props.recordedAt})` : ''}`;
          if (popupRef.current) popupRef.current.remove();
          popupRef.current = new mapboxgl.Popup()
            .setLngLat(e.lngLat)
            .setHTML(
              `<strong>${props.name}</strong><br/><span style="font-size:12px">${label}</span>`
            )
            .addTo(map);
        });
        map.on('mouseenter', 'lakes-fill', () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', 'lakes-fill', () => { map.getCanvas().style.cursor = ''; });
      }

      // Fit to all lake polygons
      const bounds = new mapboxgl.LngLatBounds();
      features.features.forEach((f) => extendBounds(bounds, f.geometry));
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 40, maxZoom: 15, duration: 0 });
      }
    };

    if (map.isStyleLoaded()) {
      applyData();
    } else {
      map.once('load', applyData);
    }

    return undefined;
  }, [features]);

  // Tear down the map on unmount
  useEffect(() => () => {
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
  }, []);

  return (
    <Box sx={{ position: 'relative', height: 420, borderRadius: 1, overflow: 'hidden' }}>
      <Box ref={containerRef} sx={{ position: 'absolute', inset: 0 }} />

      {(loading || error) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.6)',
            zIndex: 2,
          }}
        >
          {loading ? (
            <CircularProgress size={32} />
          ) : (
            <Typography color="error">{error}</Typography>
          )}
        </Box>
      )}

      {/* Legend */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 12,
          right: 12,
          bgcolor: 'rgba(255,255,255,0.92)',
          borderRadius: 1,
          p: 1.25,
          zIndex: 2,
          boxShadow: 1,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
          Infestation
        </Typography>
        <Box
          sx={{
            width: 140,
            height: 10,
            borderRadius: 1,
            background: `linear-gradient(to right, ${toHex(SCALE[0].color)}, ${toHex(SCALE[1].color)}, ${toHex(SCALE[2].color)})`,
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
          <Typography variant="caption">0%</Typography>
          <Typography variant="caption">100%</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: STALE_COLOR }} />
          <Typography variant="caption">No recent data</Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default LakeInfestationMap;
